import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { moneyFormatter, getRequestParams } from '../../helpers/utils';
import TransactionService from '../../services/transaction.service';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';

const TYPE_CHIP = {
  sale: { label: 'Sale', color: 'success' },
  cancellation: { label: 'Cancellation', color: 'default' },
  failed_payment: { label: 'Failed payment', color: 'error' },
  refund: { label: 'Refund', color: 'warning' },
  chargeback: { label: 'Chargeback', color: 'error' },
  payment_retry: { label: 'Payment retry', color: 'warning' },
  subscription_update: { label: 'Subscription update', color: 'info' }
};

const pageSizes = [10, 20, 30];

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const Transactions = () => {
  const params = useParams();
  const accountId = params.accountId;

  const [transactions, setTransactions] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [receiptLoadingUuid, setReceiptLoadingUuid] = useState(null);
  const [receiptError, setReceiptError] = useState(null);

  const onChangeSearchQuery = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const openReceipt = async (transaction) => {
    setReceiptLoadingUuid(transaction.uuid);
    setReceiptError(null);
    try {
      const { data } = await TransactionService.getReceipt(accountId, transaction.uuid);
      const url = data.invoicePdfUrl || data.hostedInvoiceUrl || data.receiptUrl;
      if (!url) {
        setReceiptError('No invoice or receipt is available for this transaction.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.log(e);
      setReceiptError(e?.response?.data?.message || 'Could not load invoice or receipt.');
    } finally {
      setReceiptLoadingUuid(null);
    }
  };

  useEffect(() => {
    setTransactions(null);
    const reqParams = getRequestParams(searchQuery, page, pageSize);
    TransactionService.getAll(accountId, reqParams)
      .then((response) => {
        setTransactions(response.data.transactions || []);
        setCount(response.data.totalPages || 0);
      })
      .catch((e) => {
        console.log(e);
        setTransactions([]);
      });
  }, [accountId, page, pageSize, searchQuery]);

  const handlePageChange = (event, value) => setPage(value);
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const searchBar = (
    <Paper component="form" sx={{ p: '2px 4px', mb: 2, display: 'flex', alignItems: 'center', width: 500, maxWidth: '100%' }} onSubmit={(e) => e.preventDefault()}>
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder="Search by product, customer name or email"
        inputProps={{ 'aria-label': 'Search by product, customer name or email' }}
        value={searchQuery}
        onChange={onChangeSearchQuery}
      />
      <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
      <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
        <SearchIcon />
      </IconButton>
    </Paper>
  );

  return (
    <div className="container">
      {searchBar}
      {transactions === null ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Box sx={{ mt: 2, mb: 2 }}>No transactions found.</Box>
      ) : (
        <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center" sx={{ width: 56 }}>Receipt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((t) => {
              const typeMeta = TYPE_CHIP[t.type] || { label: t.type, color: 'default' };
              return (
                <TableRow key={t.uuid} hover>
                  <TableCell>{formatDate(t.date)}</TableCell>
                  <TableCell>
                    <Chip label={typeMeta.label} color={typeMeta.color} size="small" />
                    {t.subscriptionCycle != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        cycle {t.subscriptionCycle}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {t.customer ? (
                      <>
                        <Typography variant="body2">{t.customer.name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.customer.email}
                        </Typography>
                      </>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {t.product?.name || t.chargeDescription || '-'}
                  </TableCell>
                  <TableCell align="right">
                    {t.amount != null ? `${t.currency?.toUpperCase() || ''} ${moneyFormatter(t.amount)}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {t.type === 'sale' ? (
                      <Tooltip title="Open invoice or receipt">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openReceipt(t)}
                            disabled={receiptLoadingUuid === t.uuid}
                          >
                            {receiptLoadingUuid === t.uuid ? (
                              <CircularProgress size={16} />
                            ) : (
                              <ReceiptLongIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid sx={{ mt: 2 }} container alignItems="center" spacing={2}>
        <FormControl size="small" sx={{ width: '6em' }}>
          <InputLabel id="transactions-page-size-label">Page size</InputLabel>
          <Select
            labelId="transactions-page-size-label"
            value={pageSize}
            label="Page size"
            onChange={handlePageSizeChange}
          >
            {pageSizes.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={count}
          page={page}
          siblingCount={1}
          boundaryCount={1}
          variant="outlined"
          shape="rounded"
          onChange={handlePageChange}
          size="large"
        />
      </Grid>
        </>
      )}
      <Snackbar
        open={Boolean(receiptError)}
        autoHideDuration={5000}
        onClose={() => setReceiptError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setReceiptError(null)}>
          {receiptError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Transactions;
